import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity,
  Database,
  Layers,
  Radio,
  Flame,
  Gauge,
  Server,
  TrendingUp,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";

/**
 * Scale Health Cockpit (`/admin/scale-health`).
 *
 * Real-time read-only window into every scale-readiness signal we
 * instrumented. Polls `/api/admin/scale-health` every 3s. Each tile
 * scores itself green / yellow / red against operator-meaningful
 * thresholds so the moment something turns yellow you know before
 * users do.
 */

const POLL_MS = 3000;

const COLORS = {
  green: { ring: "ring-emerald-300", dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", label: "Healthy" },
  yellow: { ring: "ring-amber-300", dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", label: "Watch" },
  red: { ring: "ring-rose-300", dot: "bg-rose-500", text: "text-rose-700", bg: "bg-rose-50", label: "Critical" },
  gray: { ring: "ring-zinc-300", dot: "bg-zinc-400", text: "text-zinc-600", bg: "bg-zinc-50", label: "Idle" },
};

function StatusTile({ icon: Icon, label, value, sub, level = "green", testid }) {
  const c = COLORS[level] || COLORS.gray;
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border ${c.bg} ring-1 ${c.ring} p-4 transition-shadow hover:shadow-md`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-zinc-600">
          <Icon size={14} />
          <span className="text-[11px] font-bold uppercase tracking-widest">{label}</span>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase ${c.text}`}>
          <span className={`h-2 w-2 rounded-full ${c.dot} animate-pulse`} />
          {c.label}
        </span>
      </div>
      <div className="font-display font-black text-2xl tracking-tight text-zinc-900 leading-none">
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-1.5">{sub}</div>}
    </div>
  );
}

function levelFromRange(value, { greenAtOrBelow, yellowAtOrBelow } = {}) {
  if (value == null) return "gray";
  if (value <= greenAtOrBelow) return "green";
  if (value <= yellowAtOrBelow) return "yellow";
  return "red";
}

export default function AdminScaleHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState([]); // last 60 ping samples for sparkline
  const lastTick = useRef(0);

  useEffect(() => {
    let cancelled = false;

    async function tick() {
      try {
        const { data: body } = await api.get("/admin/scale-health");
        if (cancelled) return;
        setData(body);
        setErr(null);
        const ping = body?.mongo?.ping_ms ?? null;
        if (ping != null) {
          setHistory((h) => {
            const next = [...h, ping];
            return next.length > 60 ? next.slice(next.length - 60) : next;
          });
        }
        lastTick.current = Date.now();
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail || e?.message || "Failed";
          setErr(detail);
          if (e?.response?.status === 403) {
            toast.error("Admin access required");
          }
        }
      }
    }
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const tiles = useMemo(() => {
    if (!data) return [];
    const pingMs = data.mongo?.ping_ms ?? null;
    const cacheRatio = (data.cache_stats?.hit_ratio ?? 0) * 100;
    const cacheHits = data.cache_stats?.hits ?? 0;
    const cacheMisses = data.cache_stats?.misses ?? 0;
    const buffered = data.bid_coalescer?.pending_writes ?? 0;
    const heat = data.lot_heat || {};
    const heatTracked = heat.tracked_listings ?? 0;
    const heatByLevel = heat.by_level || {};
    const frenzy = heatByLevel.frenzy || 0;
    const hot = heatByLevel.hot || 0;
    const ws = data.ws_fanout || {};
    const liveStreams = data.live_counts?.live_streams ?? 0;
    const openFrenzy = data.live_counts?.open_frenzy_moments ?? 0;

    return [
      {
        testid: "tile-mongo-ping",
        icon: Database,
        label: "Mongo round-trip",
        value: data.mongo?.ok ? `${pingMs ?? "—"} ms` : "DOWN",
        sub: data.mongo?.ok ? "ping → primary" : "Mongo unreachable — check connection",
        level: !data.mongo?.ok
          ? "red"
          : levelFromRange(pingMs, { greenAtOrBelow: 50, yellowAtOrBelow: 200 }),
      },
      {
        testid: "tile-cache-hit",
        icon: Gauge,
        label: "Cache hit ratio",
        value: cacheHits + cacheMisses === 0 ? "—" : `${cacheRatio.toFixed(0)}%`,
        sub: `${cacheHits} hits · ${cacheMisses} misses · ${data.cache_stats?.size ?? 0} keys`,
        level:
          cacheHits + cacheMisses < 5
            ? "gray"
            : cacheRatio >= 80
            ? "green"
            : cacheRatio >= 50
            ? "yellow"
            : "red",
      },
      {
        testid: "tile-bid-coalescer",
        icon: Layers,
        label: "Bid coalescer queue",
        value: buffered,
        sub: "Pending writes awaiting next 100ms flush",
        level: levelFromRange(buffered, { greenAtOrBelow: 50, yellowAtOrBelow: 200 }),
      },
      {
        testid: "tile-ws-fanout",
        icon: Radio,
        label: "WS fanout",
        value: ws.remote_bus_ready ? "Multi-pod" : ws.redis_url_configured ? "Configured" : "Single-pod",
        sub: `${ws.local_rooms ?? 0} active rooms · REDIS_URL ${ws.redis_url_configured ? "set" : "not set"}`,
        level: ws.remote_bus_ready ? "green" : ws.redis_url_configured ? "yellow" : "gray",
      },
      {
        testid: "tile-lot-heat",
        icon: Flame,
        label: "Lot heat right now",
        value: frenzy > 0 ? `🔥 ${frenzy} FRENZY` : hot > 0 ? `🔥 ${hot} HOT` : heatTracked,
        sub: `${heatTracked} lots tracked · ${heatByLevel.active || 0} active · ${hot} hot · ${frenzy} frenzy`,
        level: frenzy > 0 ? "red" : hot > 0 ? "yellow" : heatTracked > 0 ? "green" : "gray",
      },
      {
        testid: "tile-live-streams",
        icon: Activity,
        label: "Live streams",
        value: liveStreams,
        sub: `${openFrenzy} open frenzy moments mid-capture`,
        level: liveStreams > 0 ? "green" : "gray",
      },
      {
        testid: "tile-mongo-indexes",
        icon: Server,
        label: "Mongo indexes",
        value: Object.values(data.mongo_index_counts || {}).reduce((a, b) => a + (b || 0), 0),
        sub: Object.entries(data.mongo_index_counts || {})
          .map(([k, v]) => `${k}:${v ?? "?"}`)
          .join(" · "),
        level: "green",
      },
    ];
  }, [data]);

  const sparkline = useMemo(() => {
    if (history.length < 2) return null;
    const max = Math.max(...history, 5);
    const w = 300;
    const h = 48;
    const stepX = w / Math.max(1, history.length - 1);
    const pts = history
      .map((v, i) => {
        const x = i * stepX;
        const y = h - (v / max) * (h - 4) - 2;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
    return { pts, w, h, max };
  }, [history]);

  return (
    <div className="min-h-screen bg-zinc-50 pb-24" data-testid="admin-scale-health-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-3"
          data-testid="scale-health-back-admin"
        >
          <ArrowLeft size={14} /> Admin
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight">
              Scale Health Cockpit
            </h1>
            <p className="text-sm text-zinc-600 mt-1">
              Live read-out of every scale-readiness signal. Auto-refresh every 3 seconds.
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-100 px-3 py-1.5 text-sm font-bold"
            data-testid="scale-health-refresh"
          >
            <RefreshCw size={13} /> Hard refresh
          </button>
        </div>

        {err && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-4 mb-4 text-sm" data-testid="scale-health-error">
            <b>Couldn't reach /api/admin/scale-health:</b> {err}
          </div>
        )}

        {!data && !err && (
          <div className="text-sm text-zinc-500 animate-pulse">Reading instruments…</div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
              {tiles.map((t) => (
                <StatusTile key={t.testid} {...t} />
              ))}
            </div>

            {/* Mongo ping sparkline — last 60 samples (≈3 minutes) */}
            <div className="rounded-2xl border border-zinc-200 bg-white p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-zinc-500" />
                  <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-600">
                    Mongo ping — last {history.length} samples
                  </span>
                </div>
                <span className="text-[11px] text-zinc-500">
                  {sparkline ? `peak ${sparkline.max.toFixed(1)} ms` : "warming up…"}
                </span>
              </div>
              {sparkline ? (
                <svg
                  viewBox={`0 0 ${sparkline.w} ${sparkline.h}`}
                  preserveAspectRatio="none"
                  className="w-full h-12"
                  data-testid="scale-health-mongo-sparkline"
                >
                  <polyline
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="1.5"
                    points={sparkline.pts}
                  />
                </svg>
              ) : (
                <div className="h-12 grid place-items-center text-xs text-zinc-400">
                  Collecting samples…
                </div>
              )}
            </div>

            <div className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
              <b>Threshold reference</b> · Mongo ping: green ≤ 50ms, yellow ≤ 200ms ·
              Cache hit: green ≥ 80%, yellow ≥ 50% ·
              Bid coalescer queue: green ≤ 50, yellow ≤ 200 ·
              WS fanout: green = multi-pod ready, yellow = REDIS_URL set but not yet acked, gray = single-pod.
              <br />
              <span className="text-zinc-400">
                Source data: <code>/api/admin/scale-health</code> · last pulled{" "}
                {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—"}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
